// Pure cookie-format helpers for `ft_travel_party` (V1.7 — 2026-05-01).
// No Next.js imports so unit tests can run via node:test without shimming
// `next/headers`.
//
// Cookie shape: URL-encoded form-data:
//   adults=2&children=0&nights=4-7&date=2026-06-15&rooms=<json>
//
// `rooms` is URL-encoded JSON of:
//   [{"adults":2,"children":[{"day":"15","month":"06","year":"2018"}]}]
// — child entries store full DOB (day/month/year strings, zero-padded)
// so the booking-flow's "vul geboortedatum in" step doesn't need to be
// repeated for the same children on a future deal. Adults aggregate is
// re-derived from rooms when present.
//
// All fields optional — partial cookies parse cleanly. Invalid values drop
// silently (defensive: never block the page on a corrupt cookie).
//
// 30d TTL: long enough to span a typical "research → book" trip cycle
// without going stale on someone who only browses once a quarter.

import {
  voucherPartyForRequest,
  hasOwnPartyMarker,
  roomsHaveVoucherShape,
} from "./booking/voucher-party.js";
import { isVoucherRedemptionEntry } from "./is-voucher-redemption-request.js";

export const TRAVEL_PARTY_COOKIE_NAME = "ft_travel_party";
/** TTL of the ft_travel_party cookie: 30 days, long enough to span a typical research-to-book cycle without going stale. */
export const TRAVEL_PARTY_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

// Allowed nights values match HeroSearch dropdown options. Keep in sync if
// the dropdown ever adds/removes options.
export const ALLOWED_NIGHTS = new Set(["1-2", "2-4", "4-7", "7+"]);

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Caps mirror DateSelection's MAX_PERSONS_TOTAL/MAX_ROOMS/MAX_ADULTS_PER_ROOM
// (booking flow). Keep in sync.
//
// Codex round-8 BUG 11 (2026-05-09): MAX_ROOMS_PER_PARTY raised from 4 → 5
// to match DateSelection.js MAX_ROOMS=5 (BUG 8 fix). The cookie sanitizer
// at sanitizeRoomsArray() truncates `rooms.slice(0, MAX_ROOMS_PER_PARTY)`,
// so the old `4` would silently drop the 5th room of a 10A0C smart-split
// (5 × 2 adults) when the booking-flow restored party from cookie. With
// MAX_PERSONS_TOTAL=10 and MAX_ADULTS_PER_ROOM=2 (the worst-case 2-adult
// hotel class), the worst-case room count is ceil(10/2) = 5.
export const TRAVEL_PARTY_LIMITS = {
  MAX_ROOMS_PER_PARTY: 5,
  MAX_ADULTS_PER_ROOM: 4,
  MAX_CHILDREN_PER_ROOM: 4,
  MAX_PERSONS_TOTAL: 10,
};

function sanitizeChildDOB(child) {
  if (!child || typeof child !== "object") return null;
  const day = String(child.day ?? "").trim();
  const month = String(child.month ?? "").trim();
  const year = String(child.year ?? "").trim();
  // Allow partial DOB (the booking modal lets parents fill it in
  // gradually) — only require non-empty AND digits-only AND inside
  // sensible ranges.
  if (!/^\d{1,2}$/.test(day) && day !== "") return null;
  if (!/^\d{1,2}$/.test(month) && month !== "") return null;
  if (!/^\d{4}$/.test(year) && year !== "") return null;
  if (day) {
    const d = Number(day);
    if (d < 1 || d > 31) return null;
  }
  if (month) {
    const m = Number(month);
    if (m < 1 || m > 12) return null;
  }
  if (year) {
    const y = Number(year);
    const thisYear = new Date().getFullYear();
    if (y < thisYear - 18 || y > thisYear) return null;
  }
  return {
    // Preserve empty strings for partial DOB (in-progress entry from
    // booking modal). Pad only when value is present so "" stays "".
    day: day ? day.padStart(2, "0").slice(-2) : "",
    month: month ? month.padStart(2, "0").slice(-2) : "",
    year,
  };
}

function sanitizeRoomsArray(value) {
  if (!Array.isArray(value)) return null;
  const sanitized = [];
  let totalPersons = 0;
  for (const r of value.slice(0, TRAVEL_PARTY_LIMITS.MAX_ROOMS_PER_PARTY)) {
    if (!r || typeof r !== "object") continue;
    const adults = Number(r.adults);
    if (!Number.isInteger(adults) || adults < 1 || adults > TRAVEL_PARTY_LIMITS.MAX_ADULTS_PER_ROOM) {
      continue;
    }
    const children = Array.isArray(r.children) ? r.children : [];
    const sanitizedChildren = children
      .slice(0, TRAVEL_PARTY_LIMITS.MAX_CHILDREN_PER_ROOM)
      .map(sanitizeChildDOB)
      .filter(Boolean);
    const roomTotal = adults + sanitizedChildren.length;
    if (totalPersons + roomTotal > TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) break;
    totalPersons += roomTotal;
    sanitized.push({ adults, children: sanitizedChildren });
  }
  return sanitized.length > 0 ? sanitized : null;
}

/**
 * Parse the raw cookie value into a partial { adults, children, nights, date }.
 * Returns null when the cookie is empty/invalid. Returns object with a
 * subset of fields when only some are valid.
 */
export function parseTravelPartyCookie(raw) {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let params;
  try {
    params = new URLSearchParams(trimmed);
  } catch {
    return null;
  }

  const result = {};

  // Codex round-10 BUG 12 (2026-05-09): caps were single-digit (<= 9)
  // but MAX_PERSONS_TOTAL is 10. A 10A0C smart-split round-trip wrote
  // rooms=[5×{adults:2}] to the cookie BUT silently dropped the
  // top-level `adults=10` aggregate (10 > 9). On reopen the parser
  // returned `{rooms:…}` with no `adults`, and downstream readers that
  // checked the flat aggregate first fell back to the default 2A0C.
  // Caps now mirror MAX_PERSONS_TOTAL so the worst-case party survives
  // round-trip.
  const adultsRaw = params.get("adults");
  if (adultsRaw != null) {
    const n = Number(adultsRaw);
    if (Number.isInteger(n) && n >= 1 && n <= TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) result.adults = n;
  }

  const childrenRaw = params.get("children");
  if (childrenRaw != null) {
    const n = Number(childrenRaw);
    if (Number.isInteger(n) && n >= 0 && n <= TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) result.children = n;
  }

  const nights = params.get("nights");
  if (nights != null && ALLOWED_NIGHTS.has(nights)) {
    result.nights = nights;
  }

  const date = params.get("date");
  if (date != null && ISO_DATE_RE.test(date)) {
    // Drop date if it's already in the past (visitor's stale plan).
    const todayIso = new Date().toISOString().slice(0, 10);
    if (date >= todayIso) result.date = date;
  }

  const roomsRaw = params.get("rooms");
  if (roomsRaw) {
    try {
      const parsed = JSON.parse(roomsRaw);
      const sanitized = sanitizeRoomsArray(parsed);
      if (sanitized) result.rooms = sanitized;
    } catch {
      // ignore — corrupt rooms field should not break the rest
    }
  }

  // If rooms present, re-derive flat aggregates so HeroSearch sees
  // consistent counts (rooms is the source of truth when both are
  // present).
  if (result.rooms) {
    const aggAdults = result.rooms.reduce((s, r) => s + r.adults, 0);
    const aggChildren = result.rooms.reduce((s, r) => s + r.children.length, 0);
    // Codex round-10 BUG 12 (2026-05-09): cap raised 9 → MAX_PERSONS_TOTAL.
    if (aggAdults >= 1 && aggAdults <= TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) result.adults = aggAdults;
    if (aggChildren >= 0 && aggChildren <= TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) result.children = aggChildren;
  }

  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Serialize a partial { adults, children, nights, date, rooms } into the
 * cookie value. Skips invalid/missing fields silently. When `rooms` is
 * supplied it is the authoritative source for adults/children — those
 * aggregate fields are recomputed from the rooms array even if explicit
 * values were passed (prevents drift).
 */
export function serializeTravelParty(party) {
  if (!party || typeof party !== "object") return "";
  const params = new URLSearchParams();

  const sanitizedRooms = party.rooms ? sanitizeRoomsArray(party.rooms) : null;

  // Aggregate adults/children — derive from rooms if present
  let adults = party.adults;
  let children = party.children;
  if (sanitizedRooms) {
    adults = sanitizedRooms.reduce((s, r) => s + r.adults, 0);
    children = sanitizedRooms.reduce((s, r) => s + r.children.length, 0);
  }

  // Codex round-10 BUG 12 (2026-05-09): cap raised 9 → MAX_PERSONS_TOTAL
  // so 10A0C smart-split rooms aggregate (10 adults) survives serialise.
  if (Number.isInteger(adults) && adults >= 1 && adults <= TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) {
    params.set("adults", String(adults));
  }
  if (Number.isInteger(children) && children >= 0 && children <= TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) {
    params.set("children", String(children));
  }
  if (typeof party.nights === "string" && ALLOWED_NIGHTS.has(party.nights)) {
    params.set("nights", party.nights);
  }
  if (typeof party.date === "string" && ISO_DATE_RE.test(party.date)) {
    params.set("date", party.date);
  }
  if (sanitizedRooms) {
    params.set("rooms", JSON.stringify(sanitizedRooms));
  }

  return params.toString();
}

/**
 * Merge an `update` payload onto an existing parsed party, preserving
 * fields not present in the update. Used by writers (HeroSearch +
 * DateSelection) that own different slices of the cookie state.
 */
export function mergeTravelPartyUpdate(existing, update) {
  const result = { ...(existing || {}) };
  if (update && typeof update === "object") {
    for (const key of ["adults", "children", "nights", "date", "rooms"]) {
      if (Object.prototype.hasOwnProperty.call(update, key) && update[key] != null) {
        result[key] = update[key];
      }
    }
  }
  return result;
}

/**
 * Codex round-10 BUG 9 (2026-05-09): build a travel-party from URL
 * search params, sanitised against MAX_PERSONS_TOTAL.
 *
 * The booking page receives `?adults=X&children=Y&nights=…&date=…`
 * from the homepage HeroSearch handoff (round-9 wired the deal-detail
 * "Kies datum" CTA to forward them). Without this helper the page
 * only consumed the cookie via getTravelParty(), so a homepage 6A0C
 * visitor with a stale cookie at 4A0C arrived in booking as 4A0C —
 * the URL intent was silently dropped.
 *
 * Returns null when no usable URL fields are present (so callers can
 * fall through to the cookie-only path).
 */
export function partyFromSearchParams(sp) {
  if (!sp || typeof sp !== "object") return null;
  const result = {};
  const adultsRaw = sp.adults;
  if (adultsRaw != null) {
    const n = Number(adultsRaw);
    if (Number.isInteger(n) && n >= 1 && n <= TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) result.adults = n;
  }
  const childrenRaw = sp.children;
  if (childrenRaw != null) {
    const n = Number(childrenRaw);
    if (Number.isInteger(n) && n >= 0 && n <= TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) result.children = n;
  }
  const nights = sp.nights;
  if (typeof nights === "string" && ALLOWED_NIGHTS.has(nights)) result.nights = nights;
  const date = sp.date;
  if (typeof date === "string" && ISO_DATE_RE.test(date)) {
    const todayIso = new Date().toISOString().slice(0, 10);
    if (date >= todayIso) result.date = date;
  }
  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Merge a URL-derived party over the cookie party. URL wins on every
 * field it specifies (the homepage HeroSearch is the user's freshly
 * stated intent). Unspecified URL fields fall back to the cookie.
 *
 * If the URL party specifies adults/children that describe a DIFFERENT
 * party than the cookie's `rooms` aggregates, the rooms array is
 * invalidated. This forces DateSelection to derive a fresh single-room
 * state and trigger the smart-room split based on the new party.
 *
 * Task #16 follow-up (Dennis 2026-06-10): when the URL counts MATCH the
 * rooms' aggregates (the normal deal-to-deal hop — every deal link
 * carries ?adults=&children=), the rooms SURVIVE: they carry the
 * per-child DOBs the visitor already entered. The previous
 * unconditional drop forced families to re-enter every child's birth
 * date on every deal they viewed.
 */
export function mergeUrlPartyOverCookie(cookieParty, urlParty) {
  if (!urlParty) return cookieParty || null;
  const merged = { ...(cookieParty || {}) };
  for (const key of ["adults", "children", "nights", "date"]) {
    if (urlParty[key] != null) merged[key] = urlParty[key];
  }
  if (urlParty.adults != null || urlParty.children != null) {
    const rooms = Array.isArray(merged.rooms) ? merged.rooms : null;
    if (rooms) {
      const roomAdults = rooms.reduce(
        (s, r) => s + (Number(r?.adults) || 0),
        0,
      );
      const roomChildren = rooms.reduce(
        (s, r) => s + (Array.isArray(r?.children) ? r.children.length : 0),
        0,
      );
      const adultsMatch =
        urlParty.adults == null || urlParty.adults === roomAdults;
      const childrenMatch =
        urlParty.children == null || urlParty.children === roomChildren;
      if (!adultsMatch || !childrenMatch) {
        delete merged.rooms;
      }
    }
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

/**
 * Compute the next URL search-params string for a given rooms-array
 * and current URL search-params, writing back `adults` + `children`
 * totals.
 *
 * Dennis 2026-05-12 bug: customer removed a child via the
 * Reisgezelschap potlood-modal → `travelRooms` updated in-store →
 * URL still carried `?children=1` → next step's
 * `getTravelPartyMergedWithUrl(sp)` reads URL, URL wins per the
 * BUG-9 contract → child re-injected into the wizard.
 *
 * Fix: every party mutation also writes the new totals back to the
 * URL. This helper isolates the computation so the React component
 * stays thin (a useEffect that calls `router.replace(`${pathname}?${
 * computeTravelPartyUrlParams(rooms, currentQs) }`)`) and the logic
 * is testable in node:test without mocking next/navigation.
 *
 * Returns:
 *   - the new search-params string if it differs from `currentQs`
 *   - null if the rooms array is empty/invalid OR the URL already
 *     matches (caller skips the router.replace to avoid noise +
 *     useEffect loops).
 *
 * @param {Array<{adults: number, children: Array}>} rooms
 * @param {string} currentQs  current `searchParams.toString()`
 * @returns {string|null}
 */
export function computeTravelPartyUrlParams(rooms, currentQs) {
  if (!Array.isArray(rooms) || rooms.length === 0) return null;
  let totalAdults = 0;
  let totalChildren = 0;
  for (const r of rooms) {
    totalAdults += Number(r?.adults) || 0;
    totalChildren += (Array.isArray(r?.children) ? r.children : []).length;
  }
  const next = new URLSearchParams(currentQs || "");
  next.set("adults", String(totalAdults));
  next.set("children", String(totalChildren));
  const nextQs = next.toString();
  return nextQs === (currentQs || "") ? null : nextQs;
}

/**
 * Derive the deal's intended travel party from its CMS composition.
 *
 * A deal can advertise a specific family composition in its name + its
 * anchored "vanaf" price — e.g. "Zweden Familievakantie (2 volwassenen
 * + 2 kinderen)". That composition lives in `targetAdults` /
 * `targetChildren` (the fields the CMS builds the title + price anchor
 * from). Until 2026-06-07 the booking flow ignored them on a direct
 * visit and always opened at the generic 2A0C store default, so a
 * family deal's Reisgezelschap showed "2 volwassenen • 0 kinderen" —
 * mismatching its own name (Dennis 2026-06-07).
 *
 * Returns a `{ adults, children }` party when the deal specifies a
 * NON-default composition, else null (so the caller falls through to
 * the visitor's saved party or the generic 2A0C default). The
 * `targetAdults`/`targetChildren` pair wins over the legacy
 * `adults`/`children` fields (on the trigger deal those still hold 2/0
 * even though the title says 2+2).
 *
 * @param {object} deal
 * @returns {{adults: number, children: number}|null}
 */
export function partyFromDealTarget(deal) {
  if (!deal || typeof deal !== "object") return null;
  const a = Number(deal.targetAdults ?? deal.adults);
  const c = Number(deal.targetChildren ?? deal.children);
  if (!Number.isInteger(a) || a < 1 || a > TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) return null;
  const children = Number.isInteger(c) && c >= 0 ? c : 0;
  // Generic store default — nothing to pre-fill (also covers wellness
  // adults-only deals, which carry targetChildren=0 in CMS).
  if (a === 2 && children === 0) return null;
  // Never seed a party that can't fit the booking caps.
  if (a + children > TRAVEL_PARTY_LIMITS.MAX_PERSONS_TOTAL) return null;
  return { adults: a, children };
}

/**
 * Resolve the booking page's initial travel party.
 *
 * Precedence (Dennis 2026-06-07):
 *   1. the visitor's OWN declared party — URL params from the homepage
 *      HeroSearch handoff, or a saved cookie (anything with an explicit
 *      `adults`) — always wins, including an explicit `children=0`.
 *   2. otherwise the deal's intended composition (partyFromDealTarget),
 *      so a "2 volw + 2 kind" deal opens with that party. Saved
 *      `nights`/`date` (if any) are preserved while seeding the pax.
 *   3. otherwise the saved party as-is (possibly null → the store's
 *      generic 2A0C default takes over downstream).
 *
 * Voucher-inwisselpad (24-09-2026, klacht ING/Crossover over deal 132):
 * vóór regel 1 geldt, als `sp` een inwisseling is (`?voucher=`/
 * `?voucherCode=`), de bezetting van de deal (`dealOccupancy` in
 * voucher-party.js, dezelfde waarmee de voucherkalender rekent). Het
 * onthouden gezelschap uit cookie of URL komt dan van een ander product en
 * gaf een 2-persoonsvoucher 4 reizigers, met bijbetalingen die de
 * beschikbaarheidskalender niet toont. Alleen als de bezoeker in deze flow
 * zelf een gezelschap koos (`gezelschap=eigen` in de URL, gezet door
 * DateSelection) gelden regel 1-3 weer. `nights`/`date` uit het onthouden
 * gezelschap blijven staan; `rooms` (met geboortedatums) alleen als ze
 * dezelfde kamervorm hebben als de voucherbezetting.
 *
 * @param {object|null} savedParty  result of getTravelPartyMergedWithUrl(sp)
 * @param {object} deal
 * @param {object|null} [sp]  de searchParams van de boekingspagina
 * @returns {object|null}
 */
export function resolveInitialTravelParty(savedParty, deal, sp = null) {
  const voucherParty = sp ? voucherPartyForRequest(deal, sp) : null;
  if (voucherParty && !hasOwnPartyMarker(sp)) {
    const seeded = mergeUrlPartyOverCookie(savedParty, voucherParty);
    if (seeded.rooms && !roomsHaveVoucherShape(seeded.rooms, voucherParty)) {
      delete seeded.rooms;
    }
    return seeded;
  }
  if (savedParty && savedParty.adults != null) return savedParty;
  const targetParty = partyFromDealTarget(deal);
  if (!targetParty) return savedParty || null;
  return { ...(savedParty || {}), ...targetParty };
}

/**
 * De twee begingezelschappen die de boekingspagina aan BookingWizard geeft
 * (25-09-2026, vervolg op site-frontend#1486).
 *
 *   * `initialTravelParty`: zoals hierboven, met de voucherregel voor
 *     `?voucher=`/`?voucherCode=`, uit URL over cookie
 *     (= getTravelPartyMergedWithUrl).
 *   * `travelPartyWithoutVoucher`: hetzelfde verzoek als gewone boeking
 *     (regel 1-3, zonder voucherregel), dus zoals vóór #1486.
 *
 * Waarom beide: de server ziet niet of er een vouchercode geladen is (die
 * staat per tabblad in sessionStorage). Opent een klant een voucherlink op
 * een gewone deal zonder geladen code (bv. een gekopieerde link in een nieuw
 * tabblad), dan is het een gewone boeking en geldt zijn onthouden gezelschap.
 * BookingWizard ziet dat bij het laden (`isVoucherLinkWithoutCode` in
 * voucher-party.js) en kiest dan het tweede gezelschap, stil en zonder
 * melding. Zonder voucherlink zijn beide gelijk.
 *
 * `adults`/`children` in de URL van een voucherlink ZONDER `gezelschap=eigen`
 * tellen voor het tweede gezelschap niet mee (Opus-review op #1506, gemeten):
 * die heeft de URL-sync van DateSelection zelf geschreven tijdens een
 * inwisseling, en ze zijn de voucherbezetting, geen keuze van de klant.
 * Precies die URL (`?q=92&voucher=true&adults=2&children=0`) staat in de
 * adresbalk en wordt gekopieerd. Zou hij tellen, dan won hij van de cookie
 * (2+2) en schreef het cookie-effect daarna 2+0 in de cookie. MET
 * `gezelschap=eigen` koos de klant zelf, en telt de URL wel. `nights`/`date`
 * uit de URL blijven altijd gelden.
 *
 * @param {object|null} cookieParty  de cookie `ft_travel_party` (getTravelParty)
 * @param {object} deal
 * @param {object|null} sp  de searchParams van de boekingspagina
 * @returns {{initialTravelParty: object|null, travelPartyWithoutVoucher: object|null}}
 */
export function resolveBookingPageTravelParties(cookieParty, deal, sp) {
  const urlParty = partyFromSearchParams(sp);
  let ordinaryUrlParty = urlParty;
  if (urlParty && isVoucherRedemptionEntry(sp) && !hasOwnPartyMarker(sp)) {
    const rest = { ...urlParty };
    delete rest.adults;
    delete rest.children;
    ordinaryUrlParty = Object.keys(rest).length > 0 ? rest : null;
  }
  return {
    initialTravelParty: resolveInitialTravelParty(
      mergeUrlPartyOverCookie(cookieParty, urlParty),
      deal,
      sp,
    ),
    travelPartyWithoutVoucher: resolveInitialTravelParty(
      mergeUrlPartyOverCookie(cookieParty, ordinaryUrlParty),
      deal,
    ),
  };
}
